import * as React from 'react';
import { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Popover } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import {
	checkHasApprovalHierarchy,
	compareStrings,
	getCurrency,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import {
	buildEntityRequestTierView,
	describeEntityRequestApprover,
	firstPendingEntityRequestTier,
	getEntityRequestTierDisplayName,
	isEntityRequestApprovalHierarchyEnforced,
	pendingEntityRequestApprovers,
} from '../../utils/EntityRequestApprovalUtils';
import { STOPLIGHT_COLORS } from '../../utils/DataFormatterUtils';
import ApprovalHierarchyDisplayQuoteModal from '../approval_hierarchy_display_quote/ApprovalHierarchyDisplayQuoteModal';
import { ENTITY_ADD_REQUEST_STATUSES } from '../../constants/EntityAddRequestStatuses';

/**
 * Tier-by-tier approver list for the hover card — enough to answer "who is this waiting on?" without
 * opening the modal, which is where the timestamps and per-tier amount ranges live.
 */
const ApproverSummary = ({ tiers, hasSignatures }) => (
	<div className="flex flex-col" style={{ gap: '10px', maxWidth: '280px' }}>
		{tiers.map((tier, tierIndex) => {
			const pendingEmails = pendingEntityRequestApprovers(tier).map((approver) =>
				nullSafeGet('email', approver)
			);
			const approvers = nullSafeGetOrElse('approvers', tier, []);
			return (
				// Tiers are positional and two of them can carry the same label, so index by position.
				<div className="flex flex-col" key={`tier-${tierIndex}`}>
					<span className="footnoteMedium text-character-secondary">
						{getEntityRequestTierDisplayName(tier)}
					</span>
					{approvers.length === 0 ? (
						<span className="bodyregular text-character-secondary">No approvers assigned</span>
					) : (
						approvers.map((approver) => {
							const email = nullSafeGet('email', approver);
							const hasApproved = !pendingEmails.some((pendingEmail) =>
								compareStrings(pendingEmail, email)
							);
							return (
								<span
									className="bodyregular flex flex-row items-center text-character-primary"
									style={{ gap: '6px' }}
									key={email}
								>
									{hasApproved ? (
										<CheckCircleFilled style={{ color: STOPLIGHT_COLORS.pureGreen }} />
									) : null}
									{describeEntityRequestApprover(approver)}
								</span>
							);
						})
					)}
				</div>
			);
		})}
		{/* A request nobody has acted on yet — a draft, or the preview of one not yet created — has no
		    history to offer, only the tiers it would go through. */}
		<span className="footnoteregular text-character-secondary">
			{hasSignatures
				? 'Click for the full approval history.'
				: 'Click for the full approval steps.'}
		</span>
	</div>
);

/**
 * The "Approvers" / "Pending Approvers" link on a Suggested Entity request, and the tier-progress
 * modal behind it — the entity-request counterpart of the link the invoice and quote displays show.
 *
 * Renders nothing when no approval hierarchy governs the request: a company with no `entityRequest`
 * approval rule keeps the flat single-approver flow, which has no tiers to show.
 *
 * Hovering names the approvers per tier and clicking opens the full timeline, so the surfaces that
 * only have room for one line — the Suggested Entities table in particular — can stay at the link and
 * still let a user see who is holding the request up. `label` lets those callers keep the wording
 * short; by default the link says whether the listed approvers are still pending.
 *
 * The modal itself is the one quotes and the RFP award use. Entity requests carry no amount, so it
 * runs with `isNTEBased` off — the tier amount ranges and the NTE trimming have nothing to say here,
 * and every tier gates the request regardless of its `amountUpto`.
 *
 * `typographyClassName` lets a caller that drops the link into smaller running text — the request
 * form's subtitle — match the line it sits on instead of the body size the standalone surfaces use.
 */
const ApprovalHierarchyDisplayEntityRequest: FC<any> = ({
	record,
	currentUser,
	label,
	typographyClassName = 'bodyregular',
}) => {
	const [showApproversModal, setShowApproversModal] = useState(false);

	const hasApprovalHierarchy = checkHasApprovalHierarchy(record);
	const tiers = useMemo(
		() =>
			buildEntityRequestTierView(nullSafeGetOrElse('approvalHierarchy.tierDetails', record, [])),
		[record]
	);
	const pendingTier = useMemo(() => firstPendingEntityRequestTier(tiers), [tiers]);
	const hasSignatures = tiers.some(
		(tier) =>
			nullSafeGetOrElse('approvedByUsers', tier, []).length > 0 ||
			nullSafeGetOrElse('declinedByUsers', tier, []).length > 0
	);

	if (!hasApprovalHierarchy || tiers.length === 0) {
		return null;
	}

	// Mirrors the invoice display's status-driven heading: a request the hierarchy is still gating is
	// waiting on somebody, anything else is a record of who signed. Note this reads the enforcement
	// flag, not `hasApprovalHierarchy` — a request whose approvers have all been deactivated is back on
	// the flat gate, so its tiers are history even while it sits in review.
	const linkLabel =
		label ||
		(isEntityRequestApprovalHierarchyEnforced(record) &&
		nullSafeGetOrElse('status', record, '') === ENTITY_ADD_REQUEST_STATUSES.pending_review &&
		pendingTier
			? 'Pending Approvers'
			: 'Approvers');

	const showApprovers = () => setShowApproversModal(true);

	return (
		<>
			<ApprovalHierarchyDisplayQuoteModal
				isNTEBased={false}
				tiers={tiers}
				hasApprovalHierarchy={hasApprovalHierarchy}
				currentQuote={record}
				setVisible={setShowApproversModal}
				visible={showApproversModal}
				style={{}}
				exchangeRate={1}
				currency={getCurrency({ currentUser })}
				currentUser={currentUser}
				refresh={() => {}}
				syncQuote={() => Promise.resolve()}
				noApproversMessage={'No approvers are assigned to this tier.'}
			/>
			<Popover
				content={<ApproverSummary tiers={tiers} hasSignatures={hasSignatures} />}
				title={linkLabel}
			>
				<div
					className={`inline-flex flex-row items-center ${typographyClassName}`}
					style={{ cursor: 'pointer' }}
					onClick={showApprovers}
				>
					<i className="icons8-font icons8-multiple-users" />
					<span style={{ marginLeft: '8px', textDecoration: 'underline' }}>{linkLabel}</span>
				</div>
			</Popover>
		</>
	);
};

export default connect((state) => ({
	currentUser: (state as any).session.currentUser,
}))(ApprovalHierarchyDisplayEntityRequest);
